import type {
  HiringTeamRole,
  InterviewType,
  Participant,
} from '@/pages/interviews-revamp/types/interview';
import moment from 'moment';
import { Gmeet, Zoom, Teams } from '@/assets/icons/location';
import { PhoneCall01 } from '@kula-ai/icons/outline/PhoneCall01';
import { LuMapPin } from 'react-icons/lu';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import { Hackerrank } from '@/assets/icons/ats';

export const getInterviewTimeDetails = (interview: InterviewType) => {
  const { start_time, end_time, intelligence_status } = interview;

  const startMoment = moment(start_time);
  const endMoment = moment(end_time);
  const currentMoment = moment();

  const hasCandidateNoShowed =
    currentMoment?.isAfter(endMoment) &&
    interview?.meeting_status === 'candidate_no_show';

  const isBeforeInterview = currentMoment.isBefore(startMoment);
  const isInterviewInProgress =
    currentMoment.isAfter(startMoment) && currentMoment.isBefore(endMoment);
  const isAfterInterview =
    currentMoment.isAfter(endMoment) || intelligence_status === 'completed';
  const isDuringInterview =
    (currentMoment.isAfter(startMoment) &&
      currentMoment.isBefore(endMoment) &&
      intelligence_status !== 'completed') ||
    hasCandidateNoShowed;

  return {
    isBeforeInterview,
    isInterviewInProgress,
    isAfterInterview,
    isDuringInterview,
  };
};

export const getInterviewTime = ({
  startTime,
  endTime,
  timeFormat = 'hh:mm A',
  dateFormat = 'DD-MM-YYYY',
  currUserTimezone,
}) => {
  // Null checks for both startTime and endTime
  if (!startTime && !endTime) return 'Not available';

  const isSameDay = moment(startTime)
    .tz(currUserTimezone)
    ?.isSame(moment(endTime).tz(currUserTimezone), 'day');

  const formattedStartTime = startTime
    ? moment(startTime).tz(currUserTimezone)?.format(timeFormat)
    : '-';
  const formattedEndTime = endTime
    ? moment(endTime).tz(currUserTimezone)?.format(timeFormat)
    : 'Not available';

  const currentAbbr =
    TIME_ZONE_CONSTANTS[currUserTimezone]?.abbreviation || 'UTC';

  const formatedStartDate = moment(startTime)
    .tz(currUserTimezone)
    ?.format(dateFormat);

  const formatedEndDate = moment(endTime)
    .tz(currUserTimezone)
    ?.format(dateFormat);

  const interviewTimeLabel = isSameDay
    ? `${formattedStartTime} - ${formattedEndTime} ${currentAbbr}, ${formatedStartDate}`
    : `${formattedStartTime} ${currentAbbr}, ${formatedStartDate} - ${formattedEndTime} ${currentAbbr}, ${formatedEndDate}`;

  const dateLabel = isSameDay
    ? formatedStartDate
    : `${formatedStartDate} - ${formatedEndDate}`;

  const timeLabel = `${formattedStartTime} - ${formattedEndTime} ${currentAbbr}`;

  const interviewListLabel = isSameDay
    ? `${formattedStartTime} - ${formattedEndTime}, ${formatedStartDate}`
    : `${formattedStartTime}, ${formatedStartDate} - ${formattedEndTime}, ${formatedEndDate}`;

  return { interviewTimeLabel, dateLabel, timeLabel, interviewListLabel };
};

export const locationIconMap = {
  zoom: { icon: Zoom },
  google_meet: { icon: Gmeet },
  phone_interview: { icon: PhoneCall01 },
  onsite: { icon: LuMapPin },
  teams: { icon: Teams },
  hackerrank: { icon: Hackerrank },
};

interface UserWithRole {
  name: string;
  avatar: string | null;
  role: string;
  id: number;
}

export const combineUsersWithRoles = (
  rolesData: HiringTeamRole[],
  participantsData: Participant[]
): UserWithRole[] => {
  const rolePriority = [
    'job_primary_recruiter',
    'job_primary_hiring_manager',
    'job_recruiter',
    'job_hiring_manager',
    'job_coordinator',
    'job_sourcer',
    'interviewer',
  ];

  const userMap = new Map<number, UserWithRole>();

  // Populate from rolesData
  for (const role of rolesData) {
    for (const user of role.users) {
      const existing = userMap.get(user.id);
      if (
        !existing ||
        rolePriority.indexOf(role.name) < rolePriority.indexOf(existing.role)
      ) {
        userMap.set(user.id, {
          id: user.id,
          name: user.name,
          avatar: user.avatar,
          role: role.name,
        });
      }
    }
  }

  // Add participants if not already in map
  for (const participant of participantsData) {
    if (!userMap.has(participant.id)) {
      userMap.set(participant.id, {
        name: participant.name,
        avatar: participant.avatar,
        role: 'interviewer',
        id: participant.id,
      });
    }
  }

  // Convert to array and sort by role priority
  return Array.from(userMap.values()).sort(
    (a, b) => rolePriority.indexOf(a.role) - rolePriority.indexOf(b.role)
  );
};

export const responseStatusMap = {
  pending: {
    label: 'Pending',
    color: 'secondary-500',
  },
  accepted: {
    label: 'Yes',
    color: 'status-4',
  },
  tentative: {
    label: 'May be',
    color: 'status-2',
  },
  declined: {
    label: 'No',
    color: 'status-1',
  },
};
